import { inject } from '@angular/core'
import { API_CONFIGURATION, AUTH_TOKEN_FETCHER, AuthTokenFetcher, FullApiClientConfiguration } from '@ftr/api-shared'
import { decodeObjectStrings as sourceDecodeObjectStrings } from '@ftr/contracts/shared/sanitizer'
import { Region } from '@ftr/contracts/type/court'
import { Temporal } from '@js-joda/core'
import { AppRouter, ClientArgs, InitClientReturn, initClient, tsRestFetchApi } from '@ts-rest/core'

export abstract class AnnotationsApiClientFactoryBase {
  private readonly authTokenFetcher = inject<AuthTokenFetcher>(AUTH_TOKEN_FETCHER)
  private readonly config = inject<FullApiClientConfiguration>(API_CONFIGURATION)

  protected buildClient<Contract extends AppRouter>(
    region: Region,
    contract: Contract,
    contractName: string,
  ): InitClientReturn<Contract, ClientArgs> {
    const endpointMap = this.config.annotationsApi.endpointMap
    const isKnownRegion = (Object.values(Region) as string[]).includes(region as string)
    const baseUrl = isKnownRegion ? endpointMap[region] : undefined

    if (!baseUrl) {
      const configuredRegions = Object.keys(endpointMap).join(', ') || '<none>'
      throw new Error(
        `Annotations API base URL is not configured for region "${region}" ` +
          `(contract: ${contractName}, configured regions: ${configuredRegions})`,
      )
    }

    return initClient(contract, {
      baseUrl,
      baseHeaders: {},
      api: async args => {
        const token = await this.authTokenFetcher.getToken()
        const response = await tsRestFetchApi({
          ...args,
          headers: { ...args.headers, Authorization: `Bearer ${token}` },
        })
        if (response.body) {
          response.body = decodeObjectStrings(response.body as Object)
        }
        return response
      },
    })
  }
}

const whitelistObjectTypes = [Temporal]
// Recursively decode all strings in an object
// This is a light re-implementation of the decodeObjectStrings function from @ftr/contracts/shared/sanitizer to skip decoding of certain types
function decodeObjectStrings(obj: Object, seen = new WeakSet()): Object {
  if (Array.isArray(obj)) {
    return obj.map(item => decodeObjectStrings(item, seen))
  }

  if (isObject(obj)) {
    if (seen.has(obj)) return obj
    seen.add(obj)

    if (whitelistObjectTypes.some(allowedType => obj instanceof allowedType)) {
      return obj
    }

    return Object.keys(obj).reduce((acc, key) => {
      return {
        ...acc,
        // eslint-disable-next-line security/detect-object-injection
        [key]: decodeObjectStrings((obj as any)[key], seen),
      }
    }, obj)
  }

  return sourceDecodeObjectStrings(obj, seen)
}

function isObject(val: unknown): boolean {
  return typeof val === 'object' && val !== null
}
